/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  Unsubscribe page — Pack J.2
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Public, no-auth page rendered at /unsubscribe?token=<signed_token>.
 *
 * Flow:
 *   1. On mount, automatically POSTs to /api/email/unsubscribe with the
 *      token from the URL. This is what the user expects — they clicked
 *      "unsubscribe" and should immediately be unsubscribed without
 *      needing to confirm. (Per Gmail one-click best practices.)
 *   2. Renders one of three states based on the result:
 *      - SUCCESS — green "You've been unsubscribed" with details
 *      - ALREADY OPTED OUT — friendly "you were already opted out"
 *      - ERROR — error message + email fallback for support
 *   3. On the success / already-opted-out screens, offer a "Wait, I
 *      actually want to stay subscribed" button that POSTs to
 *      /api/email/resubscribe with the same token (it's an identity
 *      token, not a one-time action token).
 *
 * Why auto-unsubscribe on mount:
 *   - Compliance: Gmail/Yahoo bulk-sender rules want the click → opt-out
 *     to be as friction-free as possible. A two-step "click → confirm"
 *     flow is actively penalized.
 *   - UX: the user clicked unsubscribe; making them click again is
 *     annoying and they may give up + mark the email as spam instead.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Unsubscribe() {
  const [status, setStatus] = useState('loading'); // loading | success | already | error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resubLoading, setResubLoading] = useState(false);
  const [resubMessage, setResubMessage] = useState('');

  // Token from URL — read once at mount
  const token = new URLSearchParams(window.location.search).get('token') || '';

  useEffect(() => {
    document.title = 'Unsubscribe · Utah Realty Photos';
    if (!token) {
      setStatus('error');
      setMessage('No unsubscribe token provided. If you clicked a link from one of our emails and ended up here, please email info@utahrealtyphotos.com and we\'ll opt you out manually.');
      return;
    }
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/email/unsubscribe`, { token });
        const d = res.data || {};
        if (d.success === true) {
          setEmail(d.email || '');
          if (d.alreadyOptedOut) {
            setStatus('already');
            setMessage(d.message || 'You were already unsubscribed.');
          } else {
            setStatus('success');
            setMessage(d.message || 'You\'ve been unsubscribed from marketing emails.');
          }
        } else {
          setStatus('error');
          setMessage(d.message || 'This unsubscribe link is invalid or expired.');
        }
      } catch (e) {
        setStatus('error');
        setMessage('Something went wrong contacting our server. Please try again, or email info@utahrealtyphotos.com.');
      }
    })();
  }, [token]);

  const handleResubscribe = async () => {
    if (!token || resubLoading) return;
    setResubLoading(true);
    setResubMessage('');
    try {
      const res = await axios.post(`${API_URL}/email/resubscribe`, { token });
      const d = res.data || {};
      if (d.success === true) {
        setResubMessage(d.message || 'You\'re re-subscribed.');
        setStatus('resubscribed');
      } else {
        setResubMessage(d.message || 'Couldn\'t re-subscribe. Please try again or email info@utahrealtyphotos.com.');
      }
    } catch (e) {
      setResubMessage('Something went wrong. Please try again or email info@utahrealtyphotos.com.');
    } finally {
      setResubLoading(false);
    }
  };

  const wrap = (children) => (
    <div style={{ maxWidth: '560px', margin: '60px auto', padding: '0 20px', fontFamily: 'system-ui, -apple-system, sans-serif', color: '#1f2937', textAlign: 'center' }}>
      {children}
      <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '40px' }}>
        Utah Realty Photos · Eagle Mountain, Utah · <a href="https://www.utahrealtyphotos.com" style={{ color: '#9ca3af' }}>utahrealtyphotos.com</a>
      </p>
    </div>
  );

  if (status === 'loading') {
    return wrap(
      <>
        <h1 style={{ color: '#6b7280', marginBottom: '16px' }}>Processing your request...</h1>
        <p style={{ color: '#6b7280' }}>One moment.</p>
      </>
    );
  }

  if (status === 'error') {
    return wrap(
      <>
        <h1 style={{ color: '#dc2626', marginBottom: '16px' }}>Couldn't complete unsubscribe</h1>
        <p style={{ color: '#374151' }}>{message}</p>
        <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '24px' }}>
          If you need help, email <a href="mailto:info@utahrealtyphotos.com" style={{ color: '#2563eb' }}>info@utahrealtyphotos.com</a> and we'll opt you out manually.
        </p>
      </>
    );
  }

  if (status === 'resubscribed') {
    return wrap(
      <>
        <h1 style={{ color: '#059669', marginBottom: '16px' }}>You're re-subscribed</h1>
        <p style={{ color: '#374151' }}>{resubMessage}</p>
        {email && (
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            We'll keep sending marketing emails to <strong>{email}</strong>.
          </p>
        )}
      </>
    );
  }

  // success or already
  return wrap(
    <>
      <h1 style={{ color: '#059669', marginBottom: '16px' }}>
        {status === 'already' ? 'You were already unsubscribed' : 'You\'re unsubscribed'}
      </h1>
      <p style={{ color: '#374151' }}>{message}</p>
      {email && status === 'success' && (
        <p style={{ color: '#6b7280', fontSize: '14px' }}>
          We've stopped sending marketing emails to <strong>{email}</strong>.
        </p>
      )}
      <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '12px' }}>
        You'll still receive transactional emails (booking confirmations, payment receipts, password resets, etc.) — those aren't marketing.
      </p>
      <div style={{ marginTop: '32px', padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <p style={{ margin: '0 0 12px', color: '#6b7280', fontSize: '14px' }}>
          Clicked unsubscribe by mistake?
        </p>
        <button
          onClick={handleResubscribe}
          disabled={resubLoading}
          style={{
            padding: '10px 22px',
            background: resubLoading ? '#9ca3af' : '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontWeight: '600',
            fontSize: '14px',
            cursor: resubLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {resubLoading ? 'Working...' : 'Wait, re-subscribe me'}
        </button>
        {resubMessage && status !== 'resubscribed' && (
          <p style={{ marginTop: '12px', color: '#dc2626', fontSize: '13px' }}>{resubMessage}</p>
        )}
      </div>
    </>
  );
}

export default Unsubscribe;
